import { Uuid } from "#customTypes/globalTypes"
import { DatabaseObject, DataWithId, IsoDate } from "#customTypes/types"

export type StandoutCategory = "thumbsUp" | "needsWork" | "secret"
export type TagPool = "skill" | "secret"

export const STANDOUT_REACTION_TYPE = {
  LIKE: "like",
  NEEDS_WORK: "needsWork",
} as const

export type StandoutReactionType =
  (typeof STANDOUT_REACTION_TYPE)[keyof typeof STANDOUT_REACTION_TYPE]

/**
 * A reviewer's reaction to a moment on the Standout Review dashboard, keyed
 * by the reacting user (one reaction per user — a needs-work replaces that
 * user's like and vice versa). Likes notify the teacher; needs-work is
 * recorded for filtering/stats but deliberately never notifies (rollout
 * decision, Sep 2026 — that conversation happens face to face).
 */
export interface StandoutReaction {
  type: StandoutReactionType
  note?: string
  createdAt: IsoDate
}

export interface EvaluationKeywordConstructor extends DataWithId {
  id: Uuid
  pool: TagPool
  text: string
  isSeeded: boolean
  isActive: boolean
  createdBy: Uuid
  createdAt: IsoDate
  lastUpdatedAt: IsoDate
  lastUpdatedBy: Uuid
}

export type FamilyState = "pending" | "approved" | "sent"
export type StandoutSource = "scanner" | "teacherApp" | "jarvis"
export type StandoutMediaKind = "photo" | "audio"
export type FamilyReactionType =
  | "paw"
  | "star"
  | "smiley"
  | "palette"
  | "thumbsUp"
export type FamilyActorRole = "guardian" | "student"

export interface StandoutMediaEntry {
  id: Uuid
  kind: StandoutMediaKind
  storagePath: string
  createdBy: Uuid
  assistantId?: Uuid
  durationSeconds?: number
  caption?: string
}

export interface FamilyReactionEntry {
  mobileUid: string
  elementId: Uuid
  types: FamilyReactionType[]
  actorRole: FamilyActorRole
  studentFirstName: string
  updatedAt: IsoDate
}

export type QuickStoryScoreSource = "model" | "stand-in"

/**
 * How sure a quick story is safe to auto-approve. Advisory only: the
 * review card shows it, and sharing still waits for a person.
 * Tune the rubric in server/quickStoryScore.ts.
 */
export interface QuickStoryScore {
  confidence: number
  positive: boolean
  appropriate: boolean
  reason: string
  scoredAt: IsoDate
  scoredNote: string
  source: QuickStoryScoreSource
}

export interface StandoutMomentConstructor extends DataWithId {
  id: Uuid
  studentId: Uuid
  curriculumId: Uuid
  evaluationId?: Uuid
  classId?: Uuid
  classSessionId?: Uuid
  // Stamped at save time from the session (teacher quick-log path). Older
  // moments and eval-path moments infer it from the curriculum schedule.
  projectId?: Uuid
  semester: string
  // Absent means a note-only quick story.
  category?: StandoutCategory
  tagIds: Uuid[]
  note?: string
  generatedText?: string
  // Parent-shareable sentence generated from the tags (Standout Review's
  // Generate button) — distinct from `generatedText`, which is the eval
  // panel's GPT output and must never be clobbered.
  generatedNote?: string
  // Which sentence parents see. Absent means the teacher's note.
  // "generatedNote" means the Generate sentence, not both.
  familyNoteSource?: "note" | "generatedNote"
  // Which sentence was shared. Same two values as familyNoteSource.
  // Absent means no text note has been shared yet. Unshare does not clear it.
  sentNoteSource?: "note" | "generatedNote"
  // Sticky. True once the shared sentence changes after that first share.
  // Never stored as false, and never cleared. A generated sentence is SMN
  // from sentNoteSource alone, even when this is absent.
  sentNoteEdited?: true
  // Share-with-parents flag (Standout Review, LEAD_TEACHER+). Downstream
  // apps (mobile feed) key off `isShared`; sharedAt/sharedBy are the audit
  // trail. Un-sharing sets isShared back to false and leaves the stamps,
  // so a contentSweep send still counts toward the family budget.
  // Mirror of familyState: approved and sent => true, pending => false.
  // Write both fields together. New readers use familyState.
  isShared?: boolean
  // Don't-share for this message. Secret is a note category, not this.
  // True blocks family send the same way category "secret" does, even when
  // familyState is approved. Absent or false means the message can be sent.
  dontShare?: boolean
  // Free-text reason saved with Don't share. Empty when none was given.
  // Shown only while dontShare is on; Allow share leaves it as history.
  dontShareReason?: string
  sharedAt?: IsoDate | null
  sharedBy?: Uuid | null
  familyState?: FamilyState
  levelAtCapture?: string
  source?: StandoutSource
  // The class TA who wrote this moment. Same id as an entry in
  // classes.assistantIds.
  assistantId?: Uuid
  media?: StandoutMediaEntry[]
  // Dot-path writes: familyReactions.<mobileUid>|<elementId>
  // null clears. Do not use deleteField.
  familyReactions?: Record<string, FamilyReactionEntry | null>
  // Review-surface state: set by the batched "mark as viewed" action on the
  // Standout Review dashboard. Absent = never looked at by anyone.
  viewedAt?: IsoDate
  viewedBy?: Uuid
  // Advisory auto-approve check for the teacher note. Not a share.
  quickStoryScore?: QuickStoryScore
  // Written via `reactions.<userId>` field paths so concurrent reviewers
  // never clobber each other. `null` = the user cleared their reaction
  // (written instead of deleteField so the value survives cleanFormData).
  reactions?: Record<Uuid, StandoutReaction | null>
  createdBy: Uuid
  createdAt: IsoDate
  lastUpdatedAt: IsoDate
  lastUpdatedBy: Uuid
}

/**
 * Manager-facing longer-term note about a staff member (chat-stream style,
 * like evalComments). Deliberately its own collection: the users collection
 * is globally subscribed, so personnel notes must not live on user docs.
 */
export interface StaffNoteConstructor extends DatabaseObject {
  id: Uuid
  subjectUserId: Uuid
  message: string
}

export interface StandoutSummaryEntry {
  tagIds: Uuid[]
  generatedText: string
  finalizedBy: Uuid
  finalizedAt: IsoDate
}

export type StandoutSummary = Partial<
  Record<StandoutCategory, StandoutSummaryEntry>
>
